import React from "react";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import {
  EmptyState,
  EmptyStateProps,
  LoadPhase,
  SkeletonCard,
} from "../../../components/ui";
import { clientDetailPath } from "../../../routes/paths";
import type { Client } from "../../clients/types/clients.types";
import { CLIENT_STATUS_STYLES } from "../../clients/statusStyles";

/** The height the list stands in at while the dashboard is on its way — about
 *  three cards, which is as much of it as a phone shows before the fold. */
const CARDS_SKELETON = "h-[280px] rounded-card";

interface RecentClientCardsProps {
  /** The newest clients, in the order the dashboard endpoint returned them. */
  clients: Client[];
  /** Stands in for the cards on a failure and when the list is empty. */
  empty: EmptyStateProps;
  /** Where the screen is — the same contract `RecentClientsTable` works to, and
   *  the same reason: the phase stands for the whole dashboard, so this list
   *  lands with the cards above it rather than the moment its own rows do. */
  phase?: LoadPhase;
}

/**
 * The recent clients as they read on a phone: one card per client, on the
 * page's own background, under a section heading with the way to the full
 * roster beside it.
 *
 * The table needs 720px to lay its five columns out. On a phone that is a card
 * scrolled sideways inside a page — which is not a list, and on a dashboard is
 * a band the reader scrolls straight past. So the row becomes a card carrying
 * what anyone glancing at a dashboard is actually after: who joined, what for,
 * and where they stand.
 *
 * The card is a link to the client rather than a sheet of the remaining
 * columns, which is the one place the two lists differ. There are only five
 * columns and four of them are on the face of the card; what is a press away
 * on a payment — the rest of a record that has no screen of its own — is a
 * whole screen here, and a sheet repeating four fields to offer a fifth would
 * stand between the reader and it.
 *
 * Which of the two renders is `DashboardPage`'s decision — see `useIsMobile`.
 */
const RecentClientCards: React.FC<RecentClientCardsProps> = ({
  clients,
  empty,
  phase = "ready",
}) => {
  if (phase !== "ready") {
    return <SkeletonCard phase={phase} className={CARDS_SKELETON} />;
  }

  if (clients.length === 0) {
    return (
      // In a card, like every other band of this screen. Loose on the page's
      // own background the block had nothing holding it: the mark, the
      // headline and the button read as three things that had come adrift
      // between the chart above and the tab bar below, rather than as what the
      // recent-clients band has to say.
      <div className="card-surface border border-line bg-white">
        {/* `sm` rather than the page-sized type the roster's own first-run
            state wears. There the empty list *is* the screen; here it is one
            band of a dashboard, and a 22px headline inside it is louder than
            the "Recent clients" heading above it — which reads as the point of
            the screen rather than as a footnote to it. See `EmptyState`. */}
        <EmptyState {...empty} size="sm" />
      </div>
    );
  }

  return (
    <ul className="flex flex-col gap-2.5">
      {clients.map((client) => (
        <li key={client.id}>
          <Link
            to={clientDetailPath(client.id)}
            // `active:` rather than `hover:` — a phone has no pointer to
            // hover with, and the press is what wants acknowledging.
            className="flex items-center gap-3 card-surface border border-line bg-white p-3.5 transition-colors active:bg-[#F8FEFF] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
          >
            <span className="w-10 h-10 flex-shrink-0 rounded-full bg-[#DDF3E4] flex items-center justify-center">
              <span className="text-[15px] font-medium text-[#2F8E5B]">
                {client.name.charAt(0)}
              </span>
            </span>

            {/* `min-w-0` is what lets a long name shorten instead of pushing
                the status pill off the card's edge. */}
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-2">
                <span className="truncate text-[15px] font-medium text-ink">
                  {client.name}
                </span>
                <span
                  className={`flex-shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${
                    CLIENT_STATUS_STYLES[client.status]
                  }`}
                >
                  {client.status}
                </span>
              </span>
              {/* Every goal on one line, cut off by the card's edge rather
                  than counted into a "+N" the way the table does it: that
                  pill opens its full list on hover, and there is no hover
                  here for it to open on. */}
              <span className="mt-0.5 block truncate text-[13px] text-ink-muted">
                {client.goals.length > 0 ? client.goals.join(", ") : "-"}
              </span>
              <span className="mt-1 block truncate text-[12px] text-ink-subtle">
                {client.activity} · Joined {client.joinedOn}
              </span>
            </span>

            <ChevronRight
              aria-hidden
              className="w-4 h-4 flex-shrink-0 text-ink-subtle"
            />
          </Link>
        </li>
      ))}
    </ul>
  );
};

export default RecentClientCards;
